import { type ReactNode } from 'react';

import {
  Box,
  Button,
  Icons,
  LayoutCard,
  MetricsBox,
  Panel,
  Skeleton,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';
import { dashesOrNum } from '@cast/utils';

export type StartAutomationTrialBannerMetrics = {
  utilizationGainPct?: number;
  recoverableSpendLabel: string;
  safeConsolidationCount?: number;
  hasMetrics: boolean;
  isMetricsLoading: boolean;
};

type StartAutomationTrialBannerProps = {
  onStartTrial: () => void;
  /** Live cluster optimization metrics (same source as Optimize banner). */
  metrics?: StartAutomationTrialBannerMetrics;
};

type TrendDirection = 'up' | 'down';

const AUTOMATION_BENEFITS = [
  {
    title: 'Rightsize requests',
    description: 'Workloads scaled to observed CPU and memory',
  },
  {
    title: 'Eliminate resource waste',
    description: 'Idle capacity reclaimed continuously',
  },
  {
    title: 'Bin-pack onto fewer nodes',
    description: 'Pods are consolidated so underutilized nodes can be removed.',
  },
  {
    title: 'Pick more efficient nodes',
    description:
      'Right-priced instance families and sizes are chosen automatically.',
  },
] as const;

const formatMetricValue = (value?: number) =>
  dashesOrNum({
    value,
    roundBy: 0,
    decimals: 0,
    mapDashes: () => '—',
  });

const TrendIndicator = ({ direction }: { direction: TrendDirection }) => (
  <Box
    aria-hidden
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 16,
      px: 8,
      borderRadius: tokens.theme.radius.full,
      bgcolor: tokens.theme.bg.status.successStrong,
      color: tokens.theme.fg.status.onSuccessStrong,
    }}
  >
    {direction === 'up' ? (
      <Icons.ArrowUpRight size={12} weight="bold" />
    ) : (
      <Icons.ArrowDownRight size={12} weight="bold" />
    )}
  </Box>
);

const MetricPanel = ({
  label,
  value,
  unit,
  trend,
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  trend?: TrendDirection;
}) => (
  <Panel
    sx={{ flex: 1, minWidth: 0, p: 16, borderRadius: tokens.theme.radius.md }}
  >
    <MetricsBox
      size="m"
      slots={{
        label,
        value,
        unit,
        footer: trend ? <TrendIndicator direction={trend} /> : undefined,
      }}
      slotProps={{
        label: { color: tokens.theme.fg.structural.secondary },
        unit: unit
          ? {
              variant: 'P12R',
              color: tokens.theme.fg.structural.secondary,
              sx: { textTransform: 'none', letterSpacing: 0 },
            }
          : undefined,
        footer: { sx: { mt: 0 } },
      }}
    />
  </Panel>
);

const MetricPanelSkeleton = () => (
  <Panel
    sx={{ flex: 1, minWidth: 0, p: 16, borderRadius: tokens.theme.radius.md }}
  >
    <Stack gap={4}>
      <Skeleton variant="text" height={18} width="70%" />
      <Skeleton variant="rounded" height={28} width="55%" />
      <Skeleton variant="rounded" height={16} width={28} />
    </Stack>
  </Panel>
);

const BenefitRow = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <Stack>
    <Typography variant="A1" color={tokens.theme.fg.structural.primary}>
      {title}
    </Typography>
    <Typography variant="P12R" color={tokens.theme.fg.structural.secondary}>
      {description}
    </Typography>
  </Stack>
);

/**
 * Automation Overview promo card (Figma Monetization 664:46503).
 * Metrics come from cluster optimizations (same as Optimize banner).
 */
export const StartAutomationTrialBanner = ({
  onStartTrial,
  metrics,
}: StartAutomationTrialBannerProps) => {
  const isMetricsLoading = metrics?.isMetricsLoading ?? false;
  const hasMetrics = metrics?.hasMetrics ?? false;

  return (
    <LayoutCard
      testId="start-automation-trial-banner"
      padding={false}
      sx={{
        overflow: 'hidden',
        boxShadow: tokens.theme.shadows.elevation.medium,
      }}
      slotProps={{
        content: { sx: { p: 0 } },
      }}
    >
      <Stack direction="row" alignItems="stretch" sx={{ width: '100%' }}>
        <Stack gap={16} sx={{ flex: 1, minWidth: 0, p: 24 }}>
          <Stack gap={8}>
            <Typography
              variant="L12B"
              color={tokens.theme.fg.structural.secondary}
            >
              Take the next step
            </Typography>
            <Stack gap={4}>
              <Typography variant="h5">
                Act on what your reports reveal
              </Typography>
              <Typography
                variant="P12R"
                color={tokens.theme.fg.structural.secondary}
              >
                Let Cast AI adjust node capacity and workload resources as
                demand changes — helping reduce idle spend and the need for
                manual tuning.
              </Typography>
            </Stack>
          </Stack>

          {isMetricsLoading ? (
            <Stack direction="row" gap={16} alignItems="stretch">
              <MetricPanelSkeleton />
              <MetricPanelSkeleton />
              <MetricPanelSkeleton />
            </Stack>
          ) : hasMetrics && metrics ? (
            <Stack direction="row" gap={16} alignItems="stretch">
              <MetricPanel
                label="Utilization gain"
                value={formatMetricValue(metrics.utilizationGainPct)}
                unit={metrics.utilizationGainPct != null ? '%' : undefined}
                trend={metrics.utilizationGainPct != null ? 'up' : undefined}
              />
              <MetricPanel
                label="Recoverable spend"
                value={metrics.recoverableSpendLabel}
                trend={
                  metrics.recoverableSpendLabel !== '—' ? 'down' : undefined
                }
              />
              <MetricPanel
                label="Safe consolidation"
                value={formatMetricValue(metrics.safeConsolidationCount)}
                unit={
                  metrics.safeConsolidationCount != null
                    ? 'workloads'
                    : undefined
                }
                trend={
                  metrics.safeConsolidationCount != null ? 'up' : undefined
                }
              />
            </Stack>
          ) : null}

          <Button
            variant="primary"
            size="md"
            onClick={onStartTrial}
            testId="start-automation-trial-cta"
            sx={{ alignSelf: 'flex-start' }}
          >
            Start 30-day free trial
          </Button>

          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            Trial starts once. You will not be charged when the trial ends. Your
            reporting subscription continues either way.
          </Typography>
        </Stack>

        <Stack
          gap={12}
          sx={{
            width: 320,
            flexShrink: 0,
            p: 24,
            bgcolor: tokens.theme.bg.structural.surfaceRaised,
            borderLeft: `1px solid ${tokens.theme.border.structural.neutralSubtle}`,
          }}
        >
          <Typography
            variant="L12B"
            color={tokens.theme.fg.structural.secondary}
          >
            With automation you can
          </Typography>
          <Stack gap={16} sx={{ flex: 1 }}>
            {AUTOMATION_BENEFITS.map((benefit) => (
              <BenefitRow key={benefit.title} {...benefit} />
            ))}
          </Stack>
        </Stack>
      </Stack>
    </LayoutCard>
  );
};
